import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import {
  checkoutAmountCents,
  clockOn,
  formatHoldMinutes,
  holdChoices,
  holdFeeCents,
  stayDays,
  stayEnd,
  stayTotalCents,
  type CheckoutRequest,
  type MapSpot,
  type MapVenue,
  type PaymentMode,
  type CancellationPolicy,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { DateField } from '@/components/picker-field';
import { Field, INPUT } from '@/components/field';
import { OWN_CONTACT } from '@/lib/autofill';
import { dayPricesOf, ratesOf } from '@/components/map/reserve-form';
import { moneyOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';

// The policy the guest agrees to at checkout, with §13's fee rule beside it.
export function PolicyNote({
  policy,
  className,
}: {
  policy: CancellationPolicy;
  className?: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <p className={className ?? 'text-[11.5px] leading-relaxed text-muted-foreground'}>
      {t('policyLine')}: {t('policyFull')} {policy.fullHours} {t('hoursBefore')} ·{' '}
      {t('policyPartial')} {policy.partialPct}% {t('policyNone')} {policy.noneHours}{' '}
      {t('hoursBefore')}. {t('refundFeeNote')}
    </p>
  );
}

// Dates, a priced deadline, a name and an address; the total is days × spots
// plus the hold fee, and the mode decides what of it is taken online. §12, §13
export function BookModal({
  venue,
  spots,
  date,
  today,
  nowMinutes,
  paymentMode,
  cancellation,
  busy,
  onClose,
  onSubmit,
}: {
  venue: MapVenue;
  spots: readonly MapSpot[];
  date: string;
  today: string;
  nowMinutes: number;
  paymentMode: PaymentMode;
  cancellation: CancellationPolicy;
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: CheckoutRequest) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const [startsOn, setStartsOn] = useState(date);
  const [endsOn, setEndsOn] = useState(date);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [picked, setPicked] = useState<number | null>(null);

  const rates = useMemo(() => ratesOf(venue, spots), [venue, spots]);
  const clock = clockOn(startsOn, today, nowMinutes);
  const choices = useMemo(() => holdChoices(rates, venue.hold, clock), [rates, venue.hold, clock]);
  const holdMinutes =
    choices.find((choice) => choice.minutes === picked)?.minutes ?? choices[0]?.minutes;
  const lastDay = stayEnd(startsOn, endsOn);

  // The To follows the From past it, so the field never reads backwards. §12
  function moveStart(next: string): void {
    setStartsOn(next);
    setEndsOn(stayEnd(next, endsOn));
  }

  const days = stayDays(startsOn, lastDay);
  const dayPrices = dayPricesOf(venue, spots);
  const fee = holdMinutes === undefined ? 0 : holdFeeCents(rates, holdMinutes, venue.hold, clock);
  const total = stayTotalCents(dayPrices, days, fee);
  const online = checkoutAmountCents(paymentMode, dayPrices, days, fee);
  // Nothing to book where the window has closed: the button says so too. §12
  const ready =
    guestName.trim().length > 0 && guestEmail.trim().length > 0 && holdMinutes !== undefined;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!ready || holdMinutes === undefined) return;
    onSubmit({
      spotIds: spots.map((spot) => spot.id),
      startsOn,
      endsOn: lastDay,
      holdMinutes,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
      guestPhone: guestPhone.trim() === '' ? null : guestPhone.trim(),
      language,
    });
  }

  return (
    <>
      <button
        type="button"
        aria-label={t('close')}
        onClick={onClose}
        className="fixed inset-0 z-70 cursor-default bg-black/40"
      />
      <form
        role="dialog"
        aria-label={t('bookingTitle')}
        onSubmit={submit}
        className="fixed inset-x-2 top-4 bottom-4 z-80 mx-auto mt-safe mb-safe flex max-w-[440px] flex-col gap-3 overflow-y-auto rounded-2xl border bg-card p-4 shadow-2xl sm:inset-x-4"
      >
        <h3 className="text-[15px] font-bold">{t('bookingTitle')}</h3>
        <p className="flex flex-wrap gap-1.5" data-testid="book-spots">
          {spots.map((spot) => (
            <span key={spot.id} className="rounded-lg bg-secondary px-2 py-1 text-xs font-bold">
              #{spot.number}
            </span>
          ))}
        </p>

        {/* One row at every width: the fields are sized by our own text. §20 */}
        <div className="grid grid-cols-2 gap-3">
          <DateField label={t('from')} value={startsOn} min={today} onPick={moveStart} />
          <DateField label={t('to')} value={endsOn} min={startsOn} onPick={setEndsOn} />
        </div>

        {holdMinutes === undefined && (
          <p role="status" className="rounded-xl border p-3 text-[13px] text-muted-foreground">
            {t('visitorClosed')}
          </p>
        )}
        {holdMinutes !== undefined && (
          <Field label={t('heldUntil')}>
            <select
              className={INPUT}
              value={holdMinutes}
              onChange={(event) => setPicked(Number(event.target.value))}
            >
              {choices.map((choice) => (
                <option key={choice.minutes} value={choice.minutes}>
                  {formatHoldMinutes(choice.minutes)} ·{' '}
                  {choice.feeCents === 0
                    ? t('holdIncluded')
                    : `+${moneyOf(choice.feeCents, venue.currency, language)}`}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* The visitor is filling in their own details, so let the phone help. §12 */}
        <Field label={`${t('name')} *`}>
          <input
            className={INPUT}
            {...OWN_CONTACT.name}
            value={guestName}
            required
            autoFocus
            onChange={(event) => setGuestName(event.target.value)}
          />
        </Field>
        <Field label={`${t('email')} *`}>
          <input
            type="email"
            className={INPUT}
            {...OWN_CONTACT.email}
            value={guestEmail}
            required
            onChange={(event) => setGuestEmail(event.target.value)}
          />
        </Field>
        <Field label={t('phone')}>
          <input
            className={INPUT}
            {...OWN_CONTACT.phone}
            value={guestPhone}
            onChange={(event) => setGuestPhone(event.target.value)}
          />
        </Field>

        <p className="rounded-xl border p-3 text-[13px]" data-testid="book-total">
          <span className="text-muted-foreground">{t('total')}</span>{' '}
          <b>{moneyOf(total, venue.currency, language)}</b>{' '}
          <span className="text-muted-foreground">
            · {spots.length} × {days} {t('daysWord')}
            {fee > 0 && ` · ${t('holdFee')} ${moneyOf(fee, venue.currency, language)}`}
          </span>
          {online !== total && (
            <span className="mt-1 block text-muted-foreground" data-testid="book-online">
              {t('amountDue')}: <b>{moneyOf(online, venue.currency, language)}</b>
            </span>
          )}
        </p>

        <PolicyNote policy={cancellation} />

        <div className="mt-auto flex gap-2 pt-1">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" className="flex-1" disabled={busy || !ready}>
            {busy ? t('processing') : t(online > 0 ? 'payReserve' : 'reserveOnly')}
          </Button>
        </div>
      </form>
    </>
  );
}
